import type { ShareIntent } from 'expo-share-intent';

/**
 * Содержимое, отданное приложению системным меню «Поделиться» (стори 8.7).
 *
 * <p>Пришло оно из галереи, браузера или другого мессенджера — нам всё равно: дальше по коду
 * живёт одна структура, а разницу между iOS и Android разбирает нативный модуль.
 *
 * <p><b>Почему приложение принимает не всё подряд.</b> Отправить в чат мы умеем текст, ссылку и
 * снимок; ролики (транскод, ограничения по весу и разрешению) и произвольные файлы (у чата нет
 * такого типа сообщения вовсе) — нет. Поэтому и в системном меню SVYAZ показывается только для
 * того, что сможет отправить: пункт, который принимает файл и молча его теряет, хуже, чем его
 * отсутствие. Здесь стоит вторая половина того же правила — всё лишнее отсеивается и честно
 * пересчитывается в {@link IncomingShare.skippedFiles}, чтобы человеку было что сказать.
 */

/** Один принятый файл, уже с путём в виде `file://…`. */
export type SharedFile = {
  uri: string;
  fileName: string;
  mimeType: string;
  /** Размеры исходника, если система их сообщила: с ними снимок готовится к отправке быстрее. */
  widthPx: number | null;
  heightPx: number | null;
};

export type IncomingShare = {
  /** Текст или ссылка. `null`, если поделились только снимками. */
  text: string | null;
  files: SharedFile[];
  /** Сколько вложений отброшено: приложение не умеет их отправлять. */
  skippedFiles: number;
};

/**
 * Потолок на одну отправку. Столько же снимков за раз отдаёт и обычный выбор из галереи;
 * больше — это уже не «поделиться», а перенос альбома, и он должен идти своим путём.
 */
export const MAX_SHARED_FILES = 10;

/** Приложение умеет отправлять в чат только снимки. */
function isSupportedFile(mimeType: string | null | undefined): boolean {
  return (mimeType ?? '').toLowerCase().startsWith('image/');
}

/** Путь от нативного модуля приходит и с `file://`, и без него. */
function toFileUri(path: string): string {
  if (path.startsWith('file://') || path.startsWith('content://')) return path;
  return `file://${path}`;
}

function positiveOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Собирает текстовую часть.
 *
 * <p>Три источника на одно поле, и порядок здесь не случаен. Из браузера приходит ссылка
 * (`webUrl`) и заголовок страницы (`meta.title`) без текста — сообщение из одной ссылки читается
 * хуже, чем «Заголовок ↵ ссылка». Из заметки или другого мессенджера приходит текст, в котором
 * ссылка уже есть, — приписывать её второй раз незачем.
 */
function composeText(intent: ShareIntent): string | null {
  const raw = typeof intent.text === 'string' ? intent.text.trim() : '';
  const url = typeof intent.webUrl === 'string' ? intent.webUrl.trim() : '';
  const title = typeof intent.meta?.title === 'string' ? intent.meta.title.trim() : '';
  if (raw && url && !raw.includes(url)) return `${raw}\n${url}`;
  if (raw) return raw;
  if (url && title) return `${title}\n${url}`;
  if (url) return url;
  return title || null;
}

/** Приводит содержимое системного меню к тому, что приложение умеет отправить. */
export function normalizeShareIntent(intent: ShareIntent | null | undefined): IncomingShare | null {
  if (!intent) return null;

  const incoming = Array.isArray(intent.files) ? intent.files : [];
  const supported = incoming.filter((f) => isSupportedFile(f?.mimeType));
  const files: SharedFile[] = supported.slice(0, MAX_SHARED_FILES).map((f) => ({
    uri: toFileUri(f.path),
    fileName: f.fileName || 'shared-image',
    mimeType: f.mimeType,
    widthPx: positiveOrNull(f.width),
    heightPx: positiveOrNull(f.height),
  }));
  const skippedFiles = incoming.length - files.length;

  const text = composeText(intent);
  if (!text && files.length === 0) return null;
  return { text, files, skippedFiles };
}
